// La huella del proveedor en un contenedor (2026-09-10): quién y cuándo lo
// rellenó un usuario del rol Supplier desde esta misma app. La pone el servidor
// (CargoDetail.SupplierEditedBy / SupplierEditedAtUtc) y aquí solo se enseña,
// para que el controlador vea que el proveedor ya ha metido lo suyo antes de
// rematar la ficha.

export interface SupplierStamp {
  who: string;
  when: string;
}

interface Stamped {
  supplierEditedBy?: string | null;
  supplierEditedAtUtc?: string | null;
}

// La hora viaja en UTC sin sufijo "Z" (System.Text.Json serializa DateTime
// "Unspecified" tal cual); se le añade para que el navegador la pase a local.
function parseUtc(value: string): Date | null {
  const text = value.trim();
  if (!text) return null;
  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(text) ? text : `${text}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatSupplierStampDate(value: string, locale?: string): string {
  const date = parseUtc(value);
  if (!date) return value;
  try {
    return date.toLocaleString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return date.toISOString().substring(0, 16).replace('T', ' ');
  }
}

export function getSupplierStamp(detail: Stamped | null | undefined, locale?: string): SupplierStamp | null {
  const who = detail?.supplierEditedBy?.trim();
  if (!who) return null;
  const when = detail?.supplierEditedAtUtc ? formatSupplierStampDate(detail.supplierEditedAtUtc, locale) : '';
  return { who, when };
}

export interface SupplierActivity {
  /** Correo del proveedor que hizo la última edición. */
  who: string;
  /** Última edición, ya formateada en el idioma del usuario ('' si no hay hora). */
  when: string;
  /** Cuántos contenedores ha rellenado algún proveedor. */
  count: number;
}

/**
 * La huella del proveedor en la CARD de la carga: el controlador (o el admin)
 * ve que un usuario del rol Supplier ha rellenado algo, quién fue el último y
 * cuándo (2026-09-10). Null si ningún proveedor ha tocado la carga.
 */
export function getSupplierActivity(
  edits: ReadonlyArray<{ editedBy?: string | null; editedAtUtc?: string | null }> | null | undefined,
  locale?: string,
): SupplierActivity | null {
  const stamped = (edits ?? []).filter((e) => (e.editedBy ?? '').trim() !== '');
  if (stamped.length === 0) return null;
  const last = [...stamped].sort((a, b) => {
    const ta = a.editedAtUtc ? parseUtc(a.editedAtUtc)?.getTime() ?? 0 : 0;
    const tb = b.editedAtUtc ? parseUtc(b.editedAtUtc)?.getTime() ?? 0 : 0;
    return tb - ta;
  })[0];
  return {
    who: (last.editedBy ?? '').trim(),
    when: last.editedAtUtc ? formatSupplierStampDate(last.editedAtUtc, locale) : '',
    count: stamped.length,
  };
}
